import React, { useMemo, useState } from 'react';
import Selectdrop from 'react-select';
import { AlertRecipient, getInitials, hasOfficeNumber } from '../../utils/alertRecipients';
import '../select/select.css';
import './alertRecipientsCard.css';

type Channel = 'email' | 'sms' | 'voice';

const VOICE_UNAVAILABLE_TOOLTIP =
  'Users with no office number cannot be selected for voice alert. Set a valid number in Users → Edit → Office Number.';

interface AvailableUser {
  _id: string;
  name: string;
  officenumber?: string;
}
interface Props {
  title: string;
  recipients: AlertRecipient[];
  availableUsers: AvailableUser[];
  disabled?: boolean;
  onChange: (recipients: AlertRecipient[]) => void;
}

const EmailIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="M22 6L12 13 2 6" />
  </svg>
);

const SmsIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
    <line x1="8" y1="9" x2="16" y2="9" />
    <line x1="8" y1="13" x2="13" y2="13" />
  </svg>
);

const VoiceIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
  </svg>
);

const channelIcons: Record<Channel, React.ReactNode> = {
  email: <EmailIcon />,
  sms: <SmsIcon />,
  voice: <VoiceIcon />,
};

const AlertRecipientsCard: React.FC<Props> = ({
  title,
  recipients,
  availableUsers,
  disabled = false,
  onChange,
}) => {
  const [addSelectKey, setAddSelectKey] = useState(0);
  const recipientIds = new Set(recipients.map((r) => r._id));
  const addableUsers = (availableUsers || []).filter((u) => u._id && !recipientIds.has(u._id));

  const userOfficeNumberMap = useMemo(() => {
    const map = new Map<string, boolean>();
    (availableUsers || []).forEach((user) => {
      if (user._id) {
        map.set(user._id, hasOfficeNumber(user.officenumber));
      }
    });
    return map;
  }, [availableUsers]);

  const canUseVoice = (userId: string) => userOfficeNumberMap.get(userId) === true;

  const handleToggle = (userId: string, channel: Channel) => {
    if (channel === 'voice' && !canUseVoice(userId)) return;

    const updated = recipients.map((recipient) => {
      if (recipient._id !== userId) return recipient;
      return { ...recipient, [channel]: !recipient[channel] };
    }).filter((recipient) => recipient.email || recipient.sms || recipient.voice);

    onChange(updated);
  };

  const handleRemove = (userId: string) => {
    onChange(recipients.filter((r) => r._id !== userId));
  };

  const handleAddRecipient = (user: { _id: string; name: string }) => {
    if (!user?._id || recipientIds.has(user._id)) return;
    onChange([
      ...recipients,
      { _id: user._id, name: user.name, email: true, sms: false, voice: false },
    ]);
    setAddSelectKey((key) => key + 1);
  };

  const renderChannelToggle = (recipient: AlertRecipient, channel: Channel) => {
    const isVoice = channel === 'voice';
    const voiceUnavailable = isVoice && !canUseVoice(recipient._id);
    const isToggleDisabled = disabled || voiceUnavailable;
    const tooltip = voiceUnavailable ? VOICE_UNAVAILABLE_TOOLTIP : channel;

    const button = (
      <button
        type="button"
        className={`alert-recipients-toggle${recipient[channel] && !voiceUnavailable ? ' active' : ''}${voiceUnavailable ? ' unavailable' : ''}`}
        onClick={() => handleToggle(recipient._id, channel)}
        disabled={isToggleDisabled}
        title={voiceUnavailable ? undefined : tooltip}
      >
        {channelIcons[channel]}
      </button>
    );

    if (voiceUnavailable) {
      return (
        <span key={channel} className="alert-recipients-toggle-wrap" title={tooltip}>
          {button}
        </span>
      );
    }

    return <React.Fragment key={channel}>{button}</React.Fragment>;
  };

  return (
    <div className="alert-recipients-section">
      <h6 className="alert-recipients-section-title">{title}</h6>
      <div className={`alert-recipients-card${disabled ? ' disabled' : ''}`}>
        <div className="alert-recipients-card-body">
          {recipients.length === 0 ? (
            <div className="alert-recipients-empty">No recipients yet</div>
          ) : (
            recipients.map((recipient) => (
              <div key={recipient._id} className="alert-recipients-row">
                <div className="alert-recipients-avatar">{getInitials(recipient.name)}</div>
                <span className="alert-recipients-name">{recipient.name}</span>
                <div className="alert-recipients-toggles">
                  {(['email', 'sms', 'voice'] as Channel[]).map((channel) => renderChannelToggle(recipient, channel))}
                </div>
                <button
                  type="button"
                  className="alert-recipients-remove"
                  onClick={() => handleRemove(recipient._id)}
                  disabled={disabled}
                  title="Remove recipient"
                >
                  <i className="fa fa-times" />
                </button>
              </div>
            ))
          )}
        </div>

        <div className="alert-recipients-card-footer">
          <Selectdrop
            key={addSelectKey}
            classNamePrefix="custom-scroll-select"
            isDisabled={disabled || addableUsers.length === 0}
            name={`add-recipient-${title}`}
            value={null}
            onChange={(option: any) => {
              if (option) {
                handleAddRecipient({ _id: option.value, name: option.label });
              }
            }}
            options={addableUsers.map((u) => ({ value: u._id, label: u.name }))}
            placeholder="Add recipient..."
            isClearable={false}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default AlertRecipientsCard;